/*
    Trailblazer experiment: TBLZ-76 WAC Global Nav Product Recommendations Experiment
    go/trailblazerexperiments
*/

import { useMemo, useState, useCallback, useEffect } from "react";

import { defineMessage, useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { ProductRecommendationProps } from "../common/types";

import { useAnalytics } from "./use-analytics";
import { useFetchRecommendations } from "./use-fetch-recommendations";
import { useHasEnterpriseEdition } from "./use-has-enterprise-edition";

export const FEATURE_FLAG_ID = "wac_global_nav_targeted_recommendations";

export type Cohort = "control" | "variation" | "not-enrolled";

// Used to prevent multiple exposure events from being fired.
// Hovering over the Global Nav > Products menu will unmount and remount our component, causing the exposure event to fire multiple times in a session.
let isExposureAlreadyFired = false;

interface useGlobalNavProductRecommendationsExperiment
    extends ProductRecommendationProps {
    onGlobalNavProductRecommendationsFailed: (error: Error) => void;
    shouldShowGlobalNavProductRecommendations: boolean;
    mobileNavPageLabelFeaturedProducts: string;
}

export function useGlobalNavProductRecommendationsExperiment({
    isMobile
}: {
    isMobile?: boolean;
}) {
    const [
        isGlobalNavProductRecommendationsExperimentFailed,
        setIsGlobalNavProductRecommendationsExperimentFailed
    ] = useState(false);

    const { isUserLoggedIn } = useLoggedInUser();
    const intl = useIntl();
    const hasEnterpriseEdition = useHasEnterpriseEdition();
    const {
        getProductRecommendations,
        isRecommendationLoading,
        recommendations
    } = useFetchRecommendations({ isMobile });

    const {
        fireRenderGlobalNavProductRecommendationsFailed,
        fireFeatureExposed
    } = useAnalytics({ isMobile });

    const cohort = FeatureGates.getExperimentValue<Cohort>(
        FEATURE_FLAG_ID,
        "cohort",
        "not-enrolled",
        {
            fireExperimentExposure: false
        }
    );

    const onGlobalNavProductRecommendationsFailed = useCallback(
        (error: Error) => {
            fireRenderGlobalNavProductRecommendationsFailed(error);
            setIsGlobalNavProductRecommendationsExperimentFailed(true);
        },
        [fireRenderGlobalNavProductRecommendationsFailed]
    );

    useEffect(() => {
        if (cohort === "variation" && isUserLoggedIn && !hasEnterpriseEdition) {
            getProductRecommendations({
                setIsGlobalNavProductRecommendationsExperimentFailed
            });
        }
    }, [
        cohort,
        hasEnterpriseEdition,
        getProductRecommendations,
        isUserLoggedIn
    ]);

    useEffect(() => {
        if (
            !isExposureAlreadyFired &&
            isUserLoggedIn &&
            !hasEnterpriseEdition &&
            cohort !== "not-enrolled"
        ) {
            FeatureGates.manuallyLogExperimentExposure(FEATURE_FLAG_ID);
            fireFeatureExposed(cohort);
            isExposureAlreadyFired = true;
        }
    }, [cohort, fireFeatureExposed, hasEnterpriseEdition, isUserLoggedIn]);

    /**
     * Show the Global Nav Product Recommendations if the user is eligible, and the recommendations are loaded.
     */
    const shouldShowGlobalNavProductRecommendations = useMemo(() => {
        const nonEmptyRecosLoaded =
            !isRecommendationLoading && recommendations.length > 0;

        const shouldShowByRecommendationState =
            isRecommendationLoading || nonEmptyRecosLoaded;

        return (
            isUserLoggedIn &&
            cohort === "variation" &&
            shouldShowByRecommendationState &&
            !isGlobalNavProductRecommendationsExperimentFailed &&
            !hasEnterpriseEdition
        );
    }, [
        cohort,
        hasEnterpriseEdition,
        isGlobalNavProductRecommendationsExperimentFailed,
        isRecommendationLoading,
        isUserLoggedIn,
        recommendations.length
    ]);

    const mobileNavPageLabelFeaturedProducts = useMemo(() => {
        return intl.formatMessage(
            defineMessage({
                id: "globalnav.v2.productseveryone.title",
                defaultMessage: "Featured Products"
            })
        );
    }, [intl]);

    return {
        onGlobalNavProductRecommendationsFailed,
        shouldShowGlobalNavProductRecommendations,
        recommendations,
        isRecommendationLoading,
        mobileNavPageLabelFeaturedProducts
    };
}
